/**
 * useDiscountsByOutlet — per-outlet × provider discount breakdown.
 *
 * Returns one row per outlet with swiggy + zomato discount-given ₹, net sales,
 * discounted-order count (where exposed), and derived period-level %'s.
 *
 * % metrics are computed at the period level (sum_disc / sum_gmv), NOT as
 * averages of daily percentages — same fix as the ROAS hook.
 */
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '@/hooks/useDashboardContext';
import {
  fooddb,
  OUTLET_TO_FOODDB,
  FOODDB_TO_OUTLET,
  type AggregatorProvider,
} from '@/lib/supabase/fooddb';
import { slotsFor, type MetricConcept } from '@/lib/aggregator-metrics';
import { effectivePeriodRange, toISTDateString, type PeriodKey } from '@/lib/periods';

interface Args {
  period: PeriodKey;
  outletIds?: string[];
  providers?: AggregatorProvider[];
}

export interface DiscountPerProvider {
  discountRs: number;
  netSales: number;
  delivered: number;
  ordersWithOffer: number;
  gmvFromOffers: number;
  /** Period-level: discountRs / (netSales + discountRs) × 100. */
  discountPctOfGross: number;
  /** ₹ per delivered order, restaurant-funded. */
  perOrder: number;
}

export interface DiscountOutletRow {
  outletId: string;
  swiggy: DiscountPerProvider;
  zomato: DiscountPerProvider;
  totalDiscountRs: number;
  totalNetSales: number;
  totalDelivered: number;
  combinedPctOfGross: number;
  combinedPerOrder: number;
  /** which platform discounts MORE (higher % of gross). */
  worstPlatform: AggregatorProvider | null;
}

export interface DiscountsByOutletResult {
  rows: DiscountOutletRow[];
  totals: {
    discountRs: number;
    netSales: number;
    delivered: number;
    pctOfGross: number;
    perOrder: number;
    ordersWithOffer: number;
    gmvFromOffers: number;
  };
}

const CONCEPTS: MetricConcept[] = [
  'discount.restaurant_funded',
  'sales.net_sales',
  'sales.delivered_orders',
  'offers.orders_with_offer',
  'offers.gmv_from_offers',
];

export function useDiscountsByOutlet({ period, outletIds, providers }: Args) {
  const { customRange } = useDashboard();
  const range = effectivePeriodRange(period, customRange);
  const selectedProviders: AggregatorProvider[] = providers ?? ['swiggy', 'zomato'];
  return useQuery<DiscountsByOutletResult>({
    queryKey: [
      'discounts-by-outlet',
      range.start.toISOString(),
      outletIds?.join(',') ?? 'all',
      selectedProviders.join(','),
    ],
    queryFn: async () => {
      const targetOutlets = outletIds ?? Object.keys(OUTLET_TO_FOODDB);
      const slugs = targetOutlets.map((id) => OUTLET_TO_FOODDB[id]).filter(Boolean);

      const tuples: {
        provider: AggregatorProvider;
        category: string;
        metric: string;
        concept: MetricConcept;
      }[] = [];
      for (const provider of selectedProviders) {
        for (const concept of CONCEPTS) {
          for (const slot of slotsFor(provider, concept)) {
            tuples.push({ provider, category: slot.category, metric: slot.metric, concept });
          }
        }
      }

      const start = toISTDateString(range.start);
      const end = toISTDateString(new Date(range.end.getTime() - 1));

      const fetches = tuples.map((t) =>
        (async () => {
          const { data, error } = await fooddb
            .from('flax_metrics_daily')
            .select('outlet_id, value')
            .eq('provider', t.provider)
            .eq('category', t.category)
            .eq('metric', t.metric)
            .gte('date', start)
            .lte('date', end)
            .in('outlet_id', slugs);
          if (error) throw error;
          return {
            tuple: t,
            rows: (data ?? []) as Array<{ outlet_id: string; value: number | string }>,
          };
        })(),
      );
      const results = await Promise.all(fetches);

      type Acc = Partial<Record<MetricConcept, number>>;
      const acc: Record<string, { swiggy: Acc; zomato: Acc }> = {};
      for (const { tuple, rows } of results) {
        for (const r of rows) {
          const outletId = FOODDB_TO_OUTLET[r.outlet_id];
          if (!outletId) continue;
          if (!acc[outletId]) acc[outletId] = { swiggy: {}, zomato: {} };
          const provBucket = acc[outletId][tuple.provider];
          provBucket[tuple.concept] = (provBucket[tuple.concept] ?? 0) + Number(r.value ?? 0);
        }
      }

      const rows: DiscountOutletRow[] = targetOutlets.map((outletId) => {
        const a = acc[outletId] ?? { swiggy: {}, zomato: {} };
        const sw = derive(a.swiggy);
        const zo = derive(a.zomato);
        const totalDiscountRs = sw.discountRs + zo.discountRs;
        const totalNetSales = sw.netSales + zo.netSales;
        const totalDelivered = sw.delivered + zo.delivered;
        const combinedPctOfGross =
          totalNetSales + totalDiscountRs > 0
            ? (totalDiscountRs / (totalNetSales + totalDiscountRs)) * 100
            : 0;
        const combinedPerOrder = totalDelivered > 0 ? totalDiscountRs / totalDelivered : 0;
        const worstPlatform =
          sw.discountRs === 0 && zo.discountRs === 0
            ? null
            : sw.discountPctOfGross > zo.discountPctOfGross
              ? 'swiggy'
              : 'zomato';
        return {
          outletId,
          swiggy: sw,
          zomato: zo,
          totalDiscountRs,
          totalNetSales,
          totalDelivered,
          combinedPctOfGross,
          combinedPerOrder,
          worstPlatform,
        };
      });

      const totals = rows.reduce(
        (t, r) => ({
          discountRs: t.discountRs + r.totalDiscountRs,
          netSales: t.netSales + r.totalNetSales,
          delivered: t.delivered + r.totalDelivered,
          ordersWithOffer:
            t.ordersWithOffer + r.swiggy.ordersWithOffer + r.zomato.ordersWithOffer,
          gmvFromOffers: t.gmvFromOffers + r.swiggy.gmvFromOffers + r.zomato.gmvFromOffers,
          pctOfGross: 0,
          perOrder: 0,
        }),
        {
          discountRs: 0,
          netSales: 0,
          delivered: 0,
          ordersWithOffer: 0,
          gmvFromOffers: 0,
          pctOfGross: 0,
          perOrder: 0,
        },
      );
      totals.pctOfGross =
        totals.netSales + totals.discountRs > 0
          ? (totals.discountRs / (totals.netSales + totals.discountRs)) * 100
          : 0;
      totals.perOrder = totals.delivered > 0 ? totals.discountRs / totals.delivered : 0;

      return { rows, totals };
    },
    staleTime: 60_000,
  });
}

function derive(a: Partial<Record<MetricConcept, number>>): DiscountPerProvider {
  const discountRs = a['discount.restaurant_funded'] ?? 0;
  const netSales = a['sales.net_sales'] ?? 0;
  const delivered = a['sales.delivered_orders'] ?? 0;
  const ordersWithOffer = a['offers.orders_with_offer'] ?? 0;
  const gmvFromOffers = a['offers.gmv_from_offers'] ?? 0;
  const discountPctOfGross =
    netSales + discountRs > 0 ? (discountRs / (netSales + discountRs)) * 100 : 0;
  const perOrder = delivered > 0 ? discountRs / delivered : 0;
  return {
    discountRs,
    netSales,
    delivered,
    ordersWithOffer,
    gmvFromOffers,
    discountPctOfGross,
    perOrder,
  };
}
